import {useCallback, useState} from 'react'
import {yupResolver} from '@hookform/resolvers/yup'
import {useForm} from 'react-hook-form'

import {
  type PasswordResetFormData,
  passwordResetSchema,
} from '#/lib/schemas/password-reset'
import {cleanError} from '#/lib/strings/errors'
import {useAgent} from '#/state/session'
import {StepForgotPassword} from './StepForgotPassword.web'
import {StepNewPassword} from './StepNewPassword.web'
import {StepPasswordUpdated} from './StepPasswordUpdated.web'

type Step = 'forgotPassword' | 'setNewPassword' | 'passwordUpdated'

export function PasswordResetWizard({
  onBack,
  onDone,
}: {
  onBack: () => void
  onDone: () => void
}) {
  const agent = useAgent()
  const [step, setStep] = useState<Step>('forgotPassword')
  const [error, setError] = useState('')
  const [isProcessing, setIsProcessing] = useState(false)

  const form = useForm<PasswordResetFormData>({
    resolver: yupResolver(passwordResetSchema),
    defaultValues: {email: '', token: '', password: ''},
    mode: 'onChange',
  })

  const handleForgotPasswordNext = useCallback(async () => {
    if (isProcessing) return
    setError('')
    setIsProcessing(true)
    try {
      await agent.api.com.atproto.server.requestPasswordReset({
        email: form.getValues('email'),
      })
      setStep('setNewPassword')
    } catch (err: unknown) {
      setError(cleanError(err))
    } finally {
      setIsProcessing(false)
    }
  }, [agent, form, isProcessing])

  const handleResetPassword = useCallback(async () => {
    if (isProcessing) return
    setError('')
    setIsProcessing(true)
    try {
      await agent.api.com.atproto.server.resetPassword({
        token: form.getValues('token').trim(),
        password: form.getValues('password'),
      })
      setStep('passwordUpdated')
    } catch (err: unknown) {
      setError(cleanError(err))
    } finally {
      setIsProcessing(false)
    }
  }, [agent, form, isProcessing])

  switch (step) {
    case 'forgotPassword':
      return (
        <StepForgotPassword
          form={form}
          error={error}
          isProcessing={isProcessing}
          onBack={onBack}
          onNext={handleForgotPasswordNext}
          onHaveCode={() => {
            setError('')
            setStep('setNewPassword')
          }}
        />
      )
    case 'setNewPassword':
      return (
        <StepNewPassword
          form={form}
          error={error}
          isProcessing={isProcessing}
          email={form.getValues('email')}
          onBack={() => {
            setError('')
            setStep('forgotPassword')
          }}
          onSubmit={handleResetPassword}
        />
      )
    case 'passwordUpdated':
      return <StepPasswordUpdated onDone={onDone} />
  }
}
